import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, Tag, ArrowRight, Search, Filter, Eye, Heart } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

interface NewsItem {
  id: number;
  title: string;
  content: string;
  excerpt: string;
  author: string;
  date: string;
  category: string;
  image: string;
  views: number;
  likes: number;
  tags: string[];
  relatedLink: string;
}

const NewsPage: React.FC = () => {
  const [newsData, setNewsData] = useState<NewsItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('전체');
  const [loading, setLoading] = useState(true);
  const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [likedNews, setLikedNews] = useState<Set<number>>(() => {
    try {
      const saved = localStorage.getItem('likedNews');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch { return new Set(); }
  });

  const categories = ['전체', '공지사항', '행사', '연구성과', '교육', '협력', '시설'];

  // API에서 뉴스 데이터 로드
  useEffect(() => {
    fetchNewsData();
  }, []);

  const fetchNewsData = async () => {
    try {
      const response = await fetch('/api/news');
      const data = await response.json();
      setNewsData(data);
    } catch (error) {
      console.error('뉴스 데이터 로드 실패:', error);
      // 오류 시 기본 데이터 사용
      setNewsData([
        {
          id: 1,
          title: "인간-AI 공존 교육 헌장 제정식 개최",
          content: "서울여자대학교 인간-AI 협력 연구센터(CHAIR)에서 인간과 AI가 조화롭게 공존하는 미래를 위한 교육 헌장을 제정하고 공식 발표식을 개최했습니다.",
          excerpt: "인간과 AI가 조화롭게 공존하는 미래를 위한 교육 헌장 제정식이 성공적으로 개최되었습니다.",
          author: "CHAIR 연구센터",
          date: "2025-10-15",
          category: "공지사항",
          image: "/chair-logo.png",
          views: 156,
          likes: 23,
          tags: ["헌장", "교육", "AI윤리"],
          relatedLink: "https://chair.swu.ac.kr/charter"
        },
        {
          id: 2,
          title: "CHAIR 연구센터 신규 연구실 개소식",
          content: "인간-AI 협력 연구센터의 신규 연구실 개소식이 성대하게 개최되었습니다.",
          excerpt: "CHAIR 연구센터의 신규 연구실 개소식이 성대하게 개최되었습니다.",
          author: "관리팀",
          date: "2025-08-10",
          category: "시설",
          image: "/chair-logo.png",
          views: 167,
          likes: 28,
          tags: ["연구실", "개소식", "시설"],
          relatedLink: ""
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const filteredNews = newsData.filter(news => {
    const matchesSearch = news.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         news.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         news.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === '전체' || news.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const openNews = async (news: NewsItem) => {
    setSelectedNews(news);
    setShowModal(true);
    try {
      const res = await fetch(`/api/news/${news.id}`);
      const data = await res.json();
      setSelectedNews(data);
      setNewsData(prev => prev.map(n => n.id === news.id ? { ...n, views: data.views } : n));
    } catch (err) { /* API 실패 시 기존 데이터 유지 */ }
  };

  const handleNewsLike = async (e: React.MouseEvent, newsId: number) => {
    e.stopPropagation();
    if (likedNews.has(newsId)) return;
    try {
      const res = await fetch(`/api/news/${newsId}/like`, { method: 'POST' });
      const data = await res.json();
      setNewsData(prev => prev.map(n => n.id === newsId ? { ...n, likes: data.likes } : n));
      if (selectedNews?.id === newsId) {
        setSelectedNews(prev => prev ? { ...prev, likes: data.likes } : prev);
      }
      const newLiked = new Set(likedNews).add(newsId);
      setLikedNews(newLiked);
      localStorage.setItem('likedNews', JSON.stringify(Array.from(newLiked)));
    } catch (err) {
      console.error('좋아요 실패:', err);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="h-16" />

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-teal-500 to-navy-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold text-white mb-4">
            CHAIR 새소식
          </h1>
          <p className="text-xl text-teal-100 max-w-3xl mx-auto">
            인간-AI 협력 연구센터의 최신 소식과 활동을 확인하세요
          </p>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="새소식 검색..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>

            {/* Category Filter */}
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-gray-500" />
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      selectedCategory === category
                        ? 'bg-teal-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* News Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
              <p className="text-gray-600">새소식을 불러오는 중...</p>
            </div>
          ) : (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {filteredNews.map((news) => (
              <article key={news.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
                {/* Image */}
                <div className="relative h-48 bg-gray-200 flex items-center justify-center">
                  <img
                    src={news.image}
                    alt={news.title}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      // 연구센터 로고로 대체
                      target.src = '/chair-logo.png';
                    }}
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-teal-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                      {news.category}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-3">
                    <Calendar className="h-4 w-4 mr-1" />
                    <span>{formatDate(news.date)}</span>
                    <User className="h-4 w-4 ml-4 mr-1" />
                    <span>{news.author}</span>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
                    {news.title}
                  </h3>

                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {news.excerpt}
                  </p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {news.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs"
                      >
                        <Tag className="h-3 w-3 inline mr-1" />
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Stats and Link */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center">
                        <Eye className="h-4 w-4 mr-1" />
                        <span>{news.views}</span>
                      </div>
                      <button
                        onClick={(e) => handleNewsLike(e, news.id)}
                        className={`flex items-center gap-1 transition-colors ${likedNews.has(news.id) ? 'text-rose-500' : 'text-gray-400 hover:text-rose-500'}`}
                      >
                        <Heart className={`h-4 w-4 ${likedNews.has(news.id) ? 'fill-current' : ''}`} />
                        <span>{news.likes}</span>
                      </button>
                    </div>
                    <button
                      onClick={() => openNews(news)}
                      className="inline-flex items-center text-teal-600 hover:text-teal-800 font-medium"
                    >
                      자세히 보기
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </button>
                  </div>
                </div>
              </article>
            ))}
            </div>
          )}

          {/* No Results */}
          {!loading && filteredNews.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <Search className="h-12 w-12 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                검색 결과가 없습니다
              </h3>
              <p className="text-gray-500">
                다른 검색어나 카테고리를 시도해보세요.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* 뉴스 상세 모달 */}
      {showModal && selectedNews && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* 모달 헤더 */}
              <div className="flex justify-between items-start mb-6">
                <div className="flex-1">
                  <div className="flex items-center space-x-4 mb-3">
                    <span className="bg-teal-50 text-teal-800 px-3 py-1 rounded-full text-sm font-medium">
                      {selectedNews.category}
                    </span>
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="h-4 w-4 mr-1" />
                      <span>{formatDate(selectedNews.date)}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                      <User className="h-4 w-4 mr-1" />
                      <span>{selectedNews.author}</span>
                    </div>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    {selectedNews.title}
                  </h2>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold ml-4"
                >
                  ×
                </button>
              </div>

              {/* 모달 내용 */}
              <div className="space-y-6">
                {/* 이미지 */}
                {selectedNews.image && (
                  <div className="relative w-full bg-gray-200 rounded-lg overflow-hidden">
                    <img
                      src={selectedNews.image}
                      alt={selectedNews.title}
                      className="w-full h-auto max-h-96 object-contain"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        // 연구센터 로고로 대체
                        target.src = '/chair-logo.png';
                      }}
                    />
                  </div>
                )}

                {/* 요약 */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">요약</h3>
                  <p className="text-gray-700">{selectedNews.excerpt}</p>
                </div>

                {/* 본문 */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">상세 내용</h3>
                  <div className="prose max-w-none">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                      {selectedNews.content}
                    </p>
                  </div>
                </div>

                {/* 태그 */}
                {selectedNews.tags.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">태그</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedNews.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="bg-teal-50 text-teal-800 px-3 py-1 rounded-full text-sm"
                        >
                          <Tag className="h-3 w-3 inline mr-1" />
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* 관련 링크 */}
                {selectedNews.relatedLink && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">관련 링크</h3>
                    <a
                      href={selectedNews.relatedLink.startsWith('http') ? selectedNews.relatedLink : `${window.location.origin}${selectedNews.relatedLink}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-teal-600 hover:text-teal-800 font-medium"
                    >
                      <ArrowRight className="h-4 w-4 mr-2" />
                      관련 페이지로 이동
                    </a>
                  </div>
                )}

                {/* 통계 */}
                <div className="flex items-center space-x-6 text-sm text-gray-500 pt-4 border-t">
                  <div className="flex items-center">
                    <Eye className="h-4 w-4 mr-1" />
                    <span>조회수: {selectedNews.views}</span>
                  </div>
                  <button
                    onClick={(e) => handleNewsLike(e, selectedNews.id)}
                    className={`flex items-center gap-1 px-3 py-1 rounded-full transition-all ${likedNews.has(selectedNews.id) ? 'text-rose-500 bg-rose-50' : 'text-gray-400 hover:text-rose-500 hover:bg-rose-50'}`}
                  >
                    <Heart className={`h-4 w-4 ${likedNews.has(selectedNews.id) ? 'fill-current' : ''}`} />
                    좋아요 {selectedNews.likes}
                  </button>
                </div>
              </div>

              {/* 모달 푸터 */}
              <div className="flex justify-end mt-6 pt-4 border-t">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 bg-gray-600 text-white font-medium rounded-lg hover:bg-gray-700 transition-colors"
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default NewsPage;
